import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { Ref } from 'vue'

import type { DeviceType, BrowserType, DeviceTypes, BrowserTypes } from '@/customTypes'

export const useDeviceStore = defineStore('device', () => {
  const DeviceTypesMap: Ref<DeviceTypes> = ref({
    IPAD: 'iPad',
    IPHONE: 'iPhone',
    MOBILE: 'Mobile',
    DESKTOP: 'Desktop/Laptop',
    TABLET: 'Tablet'
  })

  const BrowserTypesMap: Ref<BrowserTypes> = ref({
    UC_BROWSER: 'UC Browser',
    EDGE: 'Edge',
    GOOGLE_BOT: 'Google Bot',
    CHROMIUM: 'Chromium',
    FIREFOX: 'Firefox',
    IE: 'IE',
    CHROME: 'Chrome',
    SAFARI: 'Safari',
    OPERA: 'Opera'
  })

  function getDeviceType() {
    const userAgent = navigator.userAgent
    if (
      /ipad/i.test(userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    ) {
      return DeviceTypesMap.value.IPAD
    }
    if (/iphone/i.test(userAgent)) {
      return DeviceTypesMap.value.IPHONE
    }
    if (/(tablet|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
      return DeviceTypesMap.value.TABLET
    }
    if (
      /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/i.test(
        userAgent
      )
    ) {
      return DeviceTypesMap.value.MOBILE
    }
    return DeviceTypesMap.value.DESKTOP
  }

  function getBrowserType(): string {
    const userAgent = navigator.userAgent
    let browser = 'unknown'
    //  UC Browser
    browser = /ucbrowser/i.test(userAgent) ? BrowserTypesMap.value.UC_BROWSER : browser
    // Edge
    browser = /edg/i.test(userAgent) ? BrowserTypesMap.value.EDGE : browser
    // Google Bot
    browser = /googlebot/i.test(userAgent) ? BrowserTypesMap.value.GOOGLE_BOT : browser
    // Chromium
    browser = /chromium/i.test(userAgent) ? BrowserTypesMap.value.CHROMIUM : browser
    // Firefox
    browser =
      /firefox|fxios/i.test(userAgent) && !/seamonkey/i.test(userAgent)
        ? BrowserTypesMap.value.FIREFOX
        : browser
    // IE
    browser =
      /msie|trident/i.test(userAgent) && !/ucbrowser/i.test(userAgent)
        ? BrowserTypesMap.value.IE
        : browser
    // Chrome
    browser =
      /chrome|crios/i.test(userAgent) &&
      !/opr|opera|chromium|edg|ucbrowser|googlebot/i.test(userAgent)
        ? BrowserTypesMap.value.CHROME
        : browser
    // Safari
    browser =
      /safari/i.test(userAgent) &&
      !/chromium|edg|ucbrowser|chrome|crios|opr|opera|fxios|firefox/i.test(userAgent)
        ? BrowserTypesMap.value.SAFARI
        : browser
    // Opera
    browser = /opr|opera/i.test(userAgent) ? BrowserTypesMap.value.OPERA : browser

    return browser
  }

  function deviceIs(type: DeviceType) {
    return getDeviceType() === DeviceTypesMap.value[type]
  }

  function browserIs(type: BrowserType) {
    return getBrowserType() === BrowserTypesMap.value[type]
  }

  return {
    DeviceTypesMap,
    BrowserTypesMap,
    getDeviceType,
    getBrowserType,
    deviceIs,
    browserIs
  }
})
